import useSiteContent from "../../lib/useSiteContent";
import { Sparkline, ACCENT, dT } from "./CsDashboard";

export function DemoTable({ title, rows, testid, extra, showTrend = true }) {
  const content = useSiteContent();
  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden" data-testid={testid}>
      <div className="px-4 py-3 border-b border-slate-100 text-sm font-medium text-slate-700">{title}</div>
      <div className={showTrend ? "overflow-x-auto" : ""}>
        <table className={`w-full text-sm ${showTrend ? "min-w-[420px]" : ""}`}>
          <thead>
            <tr className="text-left text-xs text-slate-400 border-b border-slate-100">
              <th className="py-2 px-4 font-medium">{dT(content, "cs_dash_demo_col_segment", "Segment")}</th>
              <th className="py-2 px-2 font-medium text-right">{dT(content, "cs_dash_demo_col_responses", "Responses")}</th>
              <th className="py-2 px-2 font-medium text-right">{dT(content, "cs_dash_demo_col_avg", "Avg")}</th>
              {showTrend && <th className="py-2 px-4 font-medium text-right min-w-[150px]">{dT(content, "cs_dash_demo_col_trend", "Trend")}</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label} className="border-b border-slate-50" data-testid={`${testid}-row-${r.label}`}>
                <td className={`py-2 px-4 text-slate-700 ${showTrend ? "whitespace-nowrap" : "break-words"}`}>{r.label}</td>
                <td className="py-2 px-2 text-right text-slate-500">{r.n}</td>
                <td className="py-2 px-2 text-right font-bold text-slate-800">{r.avg ?? "—"}</td>
                {showTrend && <td className="py-2 px-4 flex justify-end"><Sparkline series={r.trend} /></td>}
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={showTrend ? 4 : 3} className="py-6 text-center text-slate-400">{dT(content, "cs_dash_demo_empty", "No data yet.")}</td></tr>}
          </tbody>
        </table>
      </div>
      {extra && <div className="px-4 pb-4 pt-1 border-t border-slate-100">{extra}</div>}
    </div>
  );
}

export function Heatmap({ heatmap }) {
  const content = useSiteContent();
  if (!heatmap.age_bands.length) return <div className="text-sm text-slate-400 py-8 text-center">{dT(content, "cs_dash_heatmap_empty", "Not enough segmented data yet.")}</div>;
  const colorFor = (avg) => {
    if (avg == null) return "bg-slate-50 text-slate-300";
    if (avg >= 4.3) return "bg-emerald-500 text-white";
    if (avg >= 3.8) return "bg-emerald-300/70 text-emerald-900";
    if (avg >= 3.3) return "bg-amber-200 text-amber-900";
    return "bg-rose-400 text-white";
  };
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse min-w-[420px]">
        <thead>
          <tr>
            <th className="p-1.5 text-left text-slate-400 font-medium">{dT(content, "cs_dash_heatmap_col_age_band", "Age band")}</th>
            {heatmap.genders.map((g) => (
              <th key={g} colSpan={heatmap.cities.length} className="p-1.5 text-center text-slate-500 font-semibold border-b border-slate-100">{g}</th>
            ))}
          </tr>
          <tr>
            <th></th>
            {heatmap.genders.map((g) => heatmap.cities.map((c) => (
              <th key={g + c} className="p-1.5 text-center text-slate-400 font-medium whitespace-nowrap">{c}</th>
            )))}
          </tr>
        </thead>
        <tbody>
          {heatmap.rows.map((row) => (
            <tr key={row.age_band} data-testid={`cs-heatmap-row-${row.age_band}`}>
              <td className="p-1.5 font-semibold text-slate-600 whitespace-nowrap">{row.age_band}</td>
              {row.cells.map((cell, i) => (
                <td key={i} className="p-1">
                  <div className={`rounded-md text-center py-2 font-bold ${colorFor(cell.avg)}`}>{cell.avg ?? "—"}</div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex items-center justify-end gap-2 mt-3 text-[11px] text-slate-400" data-testid="cs-heatmap-legend">
        <span>{dT(content, "cs_dash_heatmap_legend_low", "Low Score")}</span>
        <span className="h-2 w-24 rounded-full" style={{ background: "linear-gradient(90deg, #fb7185, #fde68a, #10b981)" }} />
        <span>{dT(content, "cs_dash_heatmap_legend_high", "High Score")}</span>
      </div>
    </div>
  );
}

export function CsDashboardDemographics({ data }) {
  const content = useSiteContent();
  const d = data.demographics;
  const tables = [
    { title: dT(content, "cs_dash_demo_title_age", "By Age"), rows: d.by_age_band, testid: "cs-demo-age" },
    { title: dT(content, "cs_dash_demo_title_gender", "By Gender"), rows: d.by_gender, testid: "cs-demo-gender" },
    { title: dT(content, "cs_dash_demo_title_city", "By City"), rows: d.by_city, testid: "cs-demo-city" },
  ];
  if (data.by_location.length > 1) tables.push({ title: dT(content, "cs_dash_demo_title_location", "By Location"), rows: data.by_location, testid: "cs-demo-location" });
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {tables.map((t) => <DemoTable key={t.testid} {...t} />)}
      </div>
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5" data-testid="cs-segment-heatmap">
        <div className="text-sm font-medium text-slate-700 mb-1">{dT(content, "cs_dash_heatmap_title", "Segment Analysis Heatmap")}</div>
        <p className="text-xs text-slate-400 mb-4">{dT(content, "cs_dash_heatmap_subtitle", "Average score by age band × gender × city — greener is better.")}</p>
        <Heatmap heatmap={data.heatmap} />
      </div>
    </div>
  );
}
